import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  CreateAnnotationMutation,
  CreatePreviewAnnotationMutation,
} from '@sketch/gql-types'

import { AnnotationCommentBox } from '@sketch/modules-common'

import AnnotationPopoverWrapper from '../AnnotationPopoverWrapper'

import useCreateAnnotation from '../../operations/useCreateAnnotation'
import { Coordinates, DraftAnnotationParameters } from '../../types'

import { useAnnotationOverlayContext } from '../../hooks'
import { useIsPreviewView } from '../../../shares/views/PreviewView/PreviewContext'
import { getLatestVersionURLFromCurrentLocation } from '../../../versioning/utils'

interface DraftAnnotationPopoverProps {
  coordinates: Coordinates
  parameters: DraftAnnotationParameters
  isViewingLatestVersion: boolean
  onSubmit?: (
    data: CreateAnnotationMutation | CreatePreviewAnnotationMutation
  ) => void
}

const DraftAnnotationPopover = (props: DraftAnnotationPopoverProps) => {
  const { coordinates, parameters, onSubmit, isViewingLatestVersion } = props
  const { shareIdentifier, subject: parameterSubject } = parameters
  const isPreview = useIsPreviewView()
  const location = useLocation()
  const latestVersionUrl = getLatestVersionURLFromCurrentLocation(location)

  const {
    draftAnnotationComment,
    setDraftAnnotationComment,
    setAnnotationInFlight,
  } = useAnnotationOverlayContext() || {}

  const [commentValue, setCommentValue] = useState(draftAnnotationComment || '')

  // Throttle context updates to avoid re-rendering all consumers on every keystroke
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDraftAnnotationComment?.(commentValue)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [commentValue, setDraftAnnotationComment])

  const createAnnotation = useCreateAnnotation({
    onCacheUpdated: onSubmit,
    onComplete: onSubmit,
  })

  return (
    <AnnotationPopoverWrapper>
      <AnnotationCommentBox
        shareIdentifier={shareIdentifier}
        showOlderVersionWarning={!isViewingLatestVersion}
        latestVersionUrl={latestVersionUrl}
        onCommentChange={setCommentValue}
        comment={draftAnnotationComment || undefined}
        onCommentSubmit={async body => {
          const baseVariables = {
            shareIdentifier,
            body,
            coordinates,
          }

          setAnnotationInFlight?.(true)

          if (parameterSubject.type === 'ARTBOARD' && !isPreview) {
            const { permanentPageId, ...subject } = parameterSubject
            await createAnnotation(
              { ...baseVariables, subject },
              { permanentPageId }
            )
          } else if (parameterSubject.type === 'SECTION') {
            await createAnnotation({
              ...baseVariables,
              sectionSubject: parameterSubject,
            })
          } else {
            const subject = parameterSubject
            await createAnnotation({ ...baseVariables, subject })
          }

          setAnnotationInFlight?.(false)
        }}
        autoFocus
      />
    </AnnotationPopoverWrapper>
  )
}

export default DraftAnnotationPopover
